import {AbstractView} from "../../../views/AbstractView";
import {
  IFileUploadController,
  IT_FILE_UPLOAD_CONTROLLER,
  MK_FILE_OBJECTS,
  MK_FILE_UPLOAD_CONFIG, MK_FILE_UPLOAD_STATES
} from "../_controllers/IFileUploadController";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  Signal, untracked,
  ViewChild
} from "@angular/core";
import {FileObjectsModel} from "../_models/FileObjectsModel";
import {FileUploadConfigModel} from "../_models/FileUploadConfigModel";
import {StandardFileUploadController} from "../_controllers/standard-file-upload.controller";
import {FileObject} from "../../../interfaces/domain/FileObject";
import {FileUploadStatesModel} from "../_models/FileUploadStatesModel";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'link-file-upload',
  templateUrl: './file-upload.view.html',
  styleUrls: ['./file-upload.view.scss'],
  providers: [
    { provide: IT_FILE_UPLOAD_CONTROLLER, useClass: StandardFileUploadController }
  ]
})
export class FileUploadView extends AbstractView<IFileUploadController> {

  protected readonly fileObjectsModel: FileObjectsModel;
  protected readonly fileUploadConfigModel: FileUploadConfigModel;
  protected readonly fileUploadStatesModel: FileUploadStatesModel;

  protected readonly acceptedFileExtsAttr$: Signal<string>;

  readonly noBackend = input<boolean>();
  readonly multiple = input<boolean>();
  readonly fileSizeMB = input<number>();
  readonly acceptedFileExtensions = input<Array<string>>();
  readonly documentFileTypes = input<Array<string>>();
  readonly backendFileType = input<string>();
  readonly minWidth = input<number>();
  readonly maxWidth = input<number>();
  readonly minHeight = input<number>();
  readonly maxHeight = input<number>();
  readonly additionalRequirementMessages = input<Array<string>>();
  readonly streamingFileUpload = input<boolean>();

  readonly files = input<Array<FileObject>>();

  protected readonly onRawFileLoaded = output<File>();
  protected readonly onFileLoaded = output<FileObject>();
  protected readonly onFileRemoved = output<FileObject>();

  protected isFileOver = false;

  @ViewChild('selectedFile')
  protected selectedFile: ElementRef|undefined;

  protected overwriteErrorMessage = '';
  protected extensionErrorMessage = 'EXTENSION ERROR';
  protected sizeErrorMessage = 'SIZE ERROR';
  protected resolutionErrorMessage = 'RESOLUTION ERROR';

  protected extensionInfoMessage = '';
  protected minResolutionInfoMessage = '';
  protected maxResolutionInfoMessage = '';
  protected sizeInfoMessage = '';

  private readonly renderer: Renderer2;
  private readonly translateService: TranslateService;

  constructor() {
    super(IT_FILE_UPLOAD_CONTROLLER);

    this.renderer = inject(Renderer2);
    this.translateService = inject(TranslateService);

    this.fileObjectsModel = this.getController().getModel(MK_FILE_OBJECTS);
    this.fileUploadConfigModel = this.getController().getModel(MK_FILE_UPLOAD_CONFIG);
    this.fileUploadStatesModel = this.getController().getModel(MK_FILE_UPLOAD_STATES);

    this.acceptedFileExtsAttr$ = computed(() => {
      const fileExts = this.fileUploadConfigModel.acceptedFileExtensions$();

      if(!fileExts || fileExts.length === 0) {
        return '';
      }
      else {
        return fileExts.map((ext) => '.' + ext).join(',');
      }
    });

    effect(() => {
      const file = this.fileUploadStatesModel.rawFileLoaded$();

      if(file) {
        this.onRawFileLoaded.emit(file);
      }
    });

    effect(() => {
      const fileObject = this.fileUploadStatesModel.fileLoaded$();

      if(fileObject) {
        this.onFileLoaded.emit(fileObject);
      }
    });

    effect(() => {
      const fileObject = this.fileUploadStatesModel.fileRemoved$();

      if(fileObject) {
        this.onFileRemoved.emit(fileObject);
      }
    });

    effect(() => {
      this.getController().handle('ConfigOverwrite', {
        noBackend: this.noBackend(),
        multiple: this.multiple(),
        fileSize: this.fileSizeMB(),
        acceptedFileExtensions: this.acceptedFileExtensions(),
        documentFileTypes: this.documentFileTypes(),
        backendFileType: this.backendFileType(),
        minWidth: this.minWidth(),
        maxWidth: this.maxWidth(),
        minHeight: this.minHeight(),
        maxHeight: this.maxHeight(),
        additionalRequirementMessages: this.additionalRequirementMessages(),
        streamingFileUpload: this.streamingFileUpload(),
      });
    }, {allowSignalWrites: true});

    effect(() => {
      const files = this.files();

      untracked(() => {
        this.getController().handle('LoadFiles', {
          files: files,
        });
      });
    }, {allowSignalWrites: true});

    this.translateService.get('t.fileUploadinfo8').subscribe((result) => {
      this.overwriteErrorMessage = result;
    });

    effect(() => {
      const acceptedFileExtensions = this.fileUploadConfigModel.acceptedFileExtensions$();

      if(!acceptedFileExtensions || acceptedFileExtensions.length === 0) {
        this.extensionInfoMessage = '';
      }
      else {
        let data = acceptedFileExtensions[0];
        if(acceptedFileExtensions.length > 1) {
          data = acceptedFileExtensions.slice(0, -1).join(', ') + ' or ' + acceptedFileExtensions.slice(-1);
        }

        this.translateService.get('t.fileUploadInfo1', {acceptedExts: data}).subscribe((result) => {
          this.extensionInfoMessage = result;
        });
      }
    });

    effect(() => {
      const minWidth = this.fileUploadConfigModel.minWidth$();
      const maxWidth = this.fileUploadConfigModel.maxWidth$();
      const minHeight = this.fileUploadConfigModel.minHeight$();
      const maxHeight = this.fileUploadConfigModel.maxHeight$();

      this.populateResolutionInfoMessages(minWidth, maxWidth, minHeight, maxHeight);
    });

    effect(() => {
      const fileSize = this.fileUploadConfigModel.fileSize$();

      this.translateService.get('t.fileUploadInfo2', {fileSize: fileSize, unitOfMeasure: 'MB'}).subscribe((result) => {
        this.sizeInfoMessage = result;
      });
    });
  }

  protected populateResolutionInfoMessages(minWidth: number|null, maxWidth: number|null, minHeight: number|null, maxHeight: number|null) {
    if(minHeight && !minWidth) {
      this.translateService.get('t.fileUploadInfo3', {minHeight: minHeight}).subscribe((result) => {
        this.minResolutionInfoMessage = result;
      });
    }

    if(!minHeight && minWidth) {
      this.translateService.get('t.fileUploadInfo4', {minWidth: minWidth}).subscribe((result) => {
        this.minResolutionInfoMessage = result;
      });
    }

    if(minHeight && minWidth) {
      this.translateService.get('t.fileUploadInfo9', {minWidth: minWidth, minHeight: minHeight}).subscribe((result) => {
        this.minResolutionInfoMessage = result;
      });
    }

    if(maxHeight && !maxWidth) {
      this.translateService.get('t.fileUploadInfo5', {maxHeight: maxHeight}).subscribe((result) => {
        this.maxResolutionInfoMessage = result;
      });
    }

    if(!maxHeight && maxWidth) {
      this.translateService.get('t.fileUploadInfo6', {maxWidth: maxWidth}).subscribe((result) => {
        this.maxResolutionInfoMessage = result;
      });
    }

    if(maxHeight && maxWidth) {
      this.translateService.get('t.fileUploadInfo7', {maxWidth: maxWidth, maxHeight: maxHeight}).subscribe((result) => {
        this.maxResolutionInfoMessage = result;
      });
    }
  }

  protected onChange(fileInput: any): void {
    const target = fileInput.target || fileInput.srcElement;

    this.getController().handle('FileInput', { files: target.files });

    this.isFileOver = false;
  }

  protected onDragOver(event: any): void {
    const transfer = this.getTransfer(event);
    if(!this.haveFiles(transfer.types)) {
      return;
    }

    this.isFileOver = true;
    transfer.dropEffect = 'copy';
    this.preventAndStop(event);
  }

  protected onDragLeave(event: any): void {
    if((this as any).element) {
      if(event.currentTarget === (this as any).element[0]) {
        return;
      }
    }

    this.isFileOver = false;
    this.preventAndStop(event);
  }

  protected onDrop(event: any): void {
    const transfer = this.getTransfer(event);
    if(!transfer) {
      return;
    }

    this.preventAndStop(event);

    this.getController().handle('FileInput', { files: transfer.files });

    this.isFileOver = false;
  }

  protected getTransfer(event: any) {
    return event.dataTransfer ? event.dataTransfer : event.originalEvent.dataTransfer;
  }

  protected haveFiles(types: any) {
    if(!types) {
      return false;
    }

    if(types.indexOf) {
      return types.indexOf('Files') !== -1;
    }
    else if(types.contains) {
      return types.contains('Files');
    }
    else {
      return false;
    }
  }

  protected preventAndStop(event: any): void {
    event.preventDefault();
    event.stopPropagation();
  }

  protected onDirectDownloadClicked(fileObject: FileObject) {
    let url = '';

    if(fileObject.blob) {
      url = window.URL.createObjectURL(fileObject.blob);
    }
    else if(fileObject.url) {
      url = fileObject.url;
    }
    else {
      return;
    }

    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    a.href = url;
    a.download = fileObject.name;

    a.click();
  }

  protected onRemoveFileClicked(fileObject: FileObject) {
    this.getController().handle('RemoveFile', {fileObject: fileObject});

    this.renderer.setProperty(this.selectedFile!.nativeElement, 'value', '');
  }
}
